import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { setToken, getToken } from '@/utils/tokenManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SwitchToFullAccessModal = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter your password'); return; }
    setIsLoading(true);
    try {
      const token = getToken();
      const resp = await fetch(`${BACKEND_URL}/api/auth/upgrade-to-full`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Could not switch to full access');
      if (data.token) setToken(data.token);
      toast.success('Full access unlocked. Welcome back!');
      // Refresh so every hook picks up the new session mode.
      window.location.reload();
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="switch-full-access-modal">
        <DialogHeader className="text-center space-y-3">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-emerald-700" />
          </div>
          <DialogTitle className="text-2xl font-serif">Switch to full access</DialogTitle>
          <DialogDescription>
            Enter your account password to turn on editing. This keeps your data safe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="switch-pwd">Account password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <Input
                id="switch-pwd"
                data-testid="switch-full-access-password-input"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                autoFocus
                required
              />
              <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            data-testid="switch-full-access-submit-btn"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isLoading ? 'Unlocking…' : 'Unlock full access'}
          </Button>
          <p className="text-xs text-center text-slate-500">
            Forgot your password? You can reset it from the sign-in page.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SwitchToFullAccessModal;
